/**
 * Frontend structured logger.
 *
 * - One-line JSON in production, pretty-printed in dev.
 * - Auto-context: route, sessionId, userId (cached from Supabase session).
 * - Sanitization: same key/value rules as edge `_shared/logger.ts`.
 * - Levels: debug (dev only), info, warn, error, fatal.
 * - Pino-compatible signature: `log.info({ ... }, 'msg')`.
 *
 * Usage:
 *   import { log } from '@/lib/logger';
 *   log.info({ action: 'login.click' }, 'auth.login.attempt');
 *   try { ... } catch (err) { log.error({ err }, 'auth.login.failed'); }
 *
 * NEVER log raw `session`, `user`, headers, or request bodies — pass only the
 * specific fields you care about. The sanitizer strips known sensitive keys
 * but cannot guarantee unknown ones.
 */

import { supabase } from '@/integrations/supabase/client';

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  fatal: 50,
};

const IS_PROD =
  typeof import.meta !== 'undefined' && (import.meta as { env?: { PROD?: boolean } }).env?.PROD === true;
const MIN_LEVEL = IS_PROD ? LEVEL_ORDER.warn : LEVEL_ORDER.debug;

const SENSITIVE_KEY_RE =
  /(password|senha|passwd|token|access_token|refresh_token|id_token|jwt|authorization|cookie|set-cookie|api[_-]?key|apikey|secret|client_secret|service_role|cpf|cnpj|rg|credit[_-]?card|card[_-]?number|cvv|cvc|pin)/i;

function looksLikeSecret(value: string): boolean {
  if (value.length < 16) return false;
  if (/^eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{4,}/.test(value)) return true;
  if (/^Bearer\s+[A-Za-z0-9._-]{16,}/.test(value)) return true;
  return false;
}

const REDACTED = '[REDACTED]';
const MAX_DEPTH = 6;
const MAX_STRING_LEN = 2000;

function redact(value: unknown, depth = 0, seen = new WeakSet<object>()): unknown {
  if (value == null) return value;
  if (depth > MAX_DEPTH) return '[Truncated:depth]';
  if (typeof value === 'string') {
    if (looksLikeSecret(value)) return REDACTED;
    return value.length > MAX_STRING_LEN ? value.slice(0, MAX_STRING_LEN) + '…' : value;
  }
  if (typeof value !== 'object') return value;
  if (seen.has(value as object)) return '[Circular]';
  seen.add(value as object);
  if (Array.isArray(value)) return value.map((v) => redact(v, depth + 1, seen));
  if (value instanceof Error) {
    const code = (value as { code?: unknown }).code;
    const status = (value as { status?: unknown }).status;
    return {
      name: value.name,
      message: value.message,
      ...(typeof code === 'string' || typeof code === 'number' ? { code } : {}),
      ...(typeof status === 'number' ? { status } : {}),
      stack: value.stack?.split('\n').slice(0, 8).join('\n'),
    };
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (SENSITIVE_KEY_RE.test(k)) {
      out[k] = REDACTED;
      continue;
    }
    out[k] = redact(v, depth + 1, seen);
  }
  return out;
}

// ---- Auto-context ---------------------------------------------------------

let cachedUserId: string | null = null;
let userIdFetchedAt = 0;
const USER_TTL_MS = 30_000;

async function getUserId(): Promise<string | null> {
  if (cachedUserId && Date.now() - userIdFetchedAt < USER_TTL_MS) return cachedUserId;
  try {
    const { data } = await supabase.auth.getSession();
    cachedUserId = data.session?.user?.id ?? null;
    userIdFetchedAt = Date.now();
    return cachedUserId;
  } catch {
    return null;
  }
}

// Sync best-effort: returns last cached id without awaiting. Triggers refresh.
function getUserIdSync(): string | null {
  if (!cachedUserId || Date.now() - userIdFetchedAt > USER_TTL_MS) {
    void getUserId();
  }
  return cachedUserId;
}

function getSessionId(): string {
  if (typeof window === 'undefined') return 'ssr';
  try {
    const key = '__log_sid';
    let sid = window.sessionStorage.getItem(key);
    if (!sid) {
      sid = crypto.randomUUID();
      window.sessionStorage.setItem(key, sid);
    }
    return sid;
  } catch {
    return 'no-storage';
  }
}

function getRoute(): string {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
}

// ---- Emission -------------------------------------------------------------

function emit(level: LogLevel, payload: Record<string, unknown>, msg: string) {
  if (LEVEL_ORDER[level] < MIN_LEVEL) return;
  const entry = {
    ts: new Date().toISOString(),
    level,
    msg,
    sessionId: getSessionId(),
    userId: getUserIdSync(),
    route: getRoute(),
    ...(redact(payload) as Record<string, unknown>),
  };
  const line = IS_PROD ? JSON.stringify(entry) : entry;
  // eslint-disable-next-line no-console
  if (level === 'error' || level === 'fatal') console.error(line);
  // eslint-disable-next-line no-console
  else if (level === 'warn') console.warn(line);
  // eslint-disable-next-line no-console
  else if (level === 'debug') console.debug(line);
  // eslint-disable-next-line no-console
  else console.log(line);
}

export interface Logger {
  debug(payload: Record<string, unknown>, msg: string): void;
  info(payload: Record<string, unknown>, msg: string): void;
  warn(payload: Record<string, unknown>, msg: string): void;
  error(payload: Record<string, unknown>, msg: string): void;
  fatal(payload: Record<string, unknown>, msg: string): void;
}

export const log: Logger = {
  debug: (p, m) => emit('debug', p, m),
  info: (p, m) => emit('info', p, m),
  warn: (p, m) => emit('warn', p, m),
  error: (p, m) => emit('error', p, m),
  fatal: (p, m) => emit('fatal', p, m),
};

export function createLogger(module: string): Logger {
  return {
    debug: (p, m) => emit('debug', { module, ...p }, m),
    info: (p, m) => emit('info', { module, ...p }, m),
    warn: (p, m) => emit('warn', { module, ...p }, m),
    error: (p, m) => emit('error', { module, ...p }, m),
    fatal: (p, m) => emit('fatal', { module, ...p }, m),
  };
}

export const _internal = { redact, SENSITIVE_KEY_RE };
